import { useCallback, useMemo } from "react";
import { useActiveOrMainShippingZone } from "../providers/shippingZones";
import useSearchProductsInfiniteQuery from "../providers/products/useSearchProductsInfiniteQuery";
import _ from "lodash";
import { AdditionalFeature, Category, Product } from "@core/types";
import { useConfig } from "../providers/config";
import { useSearchParams } from "./useSearchParams";
import { useGetAllCategoriesQuery } from "../providers/categories";
import { FetchNextPageOptions, InfiniteQueryObserverResult } from "@tanstack/react-query";

export type ProductCategories = {
  categoryId: string;
  parentId: string;
  categoryName: string;
  parentName: string;
};

export type ProductSearchResult = {
  searchText: string;
  isLoading: boolean;
  isFetched: boolean;
  isFetching: boolean;
  hasNextPage: boolean | undefined;
  cleanSearch: () => void;
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<InfiniteQueryObserverResult<Product[], any>>;
  chosenCategory: string | null;
  groupedProducts: Record<string, Product[]>;
  isFetchingNextPage: boolean;
  handleChoseCategory: (categoryName: string) => void;
  getFilteredProducts: () => Product[];
  getProductCategories: () => ProductCategories[] | null;
  noResults: boolean;
};

export const useProductSearch = (): ProductSearchResult => {
  const config = useConfig();
  const minSearchLength = config.search.minSearchLength;
  const { get: getSearchParam, set: setSearchParam, delete: deleteSearchParam } = useSearchParams();

  const searchText = getSearchParam("q") || "";
  const additionalFeature = getSearchParam("additionalProductFilter");
  const chosenCategory = getSearchParam("category");

  const { activeShippingZone } = useActiveOrMainShippingZone();

  const {
    fetchNextPage,
    hasNextPage,
    isFetching: isFetchingProducts,
    isFetched,
    isFetchingNextPage,
    isLoading: isLoadingProducts,
    data: searchResult,
  } = useSearchProductsInfiniteQuery({
    params: {
      keyword: searchText,
      zoneId: activeShippingZone.id,
      additionalFeature: additionalFeature ?? undefined,
    },
    options: {
      refetchOnMount: true,
      staleTime: 1000 * 60,
      enabled: searchText.length >= minSearchLength,
    },
  });

  const {
    data: { allCategories } = {},
    isLoading: isLoadingCategories,
    isFetching: isFetchingCategories,
  } = useGetAllCategoriesQuery();

  const cleanSearch = useCallback(() => {
    deleteSearchParam("q");
    deleteSearchParam("category");
    deleteSearchParam("additionalProductFilter");
  }, [deleteSearchParam]);

  const noResults = isFetched && searchText.length >= minSearchLength && !searchResult?.pages[0]?.length;

  const groupedProducts = useMemo(() => {
    return _.groupBy(searchResult?.pages.flatMap((p) => p) || [], (p) => p.categories?.[0]?.name);
  }, [searchResult]);

  const handleChoseCategory = (categoryName: string) => {
    if (chosenCategory === categoryName) {
      deleteSearchParam("category");
    } else {
      setSearchParam("category", categoryName);
    }
  };

  const getFilteredProducts = (): Product[] => {
    if (!chosenCategory) {
      return searchResult?.pages?.flatMap((el) => el) || [];
    }
    return groupedProducts[chosenCategory] || [];
  };

  const getCategoryInfo = (categoryId: Category["id"]) => {
    const currentCategory = allCategories?.find((cat) => cat.id === categoryId);
    if (!currentCategory) return null;

    const parentCategory = allCategories?.find((cat) => cat.id === currentCategory.parent);
    if (!parentCategory) return null;

    return {
      categoryId: currentCategory.id,
      parentId: parentCategory.id,
      categoryName: currentCategory.name,
      parentName: parentCategory.name,
    };
  };

  const getProductCategories = (): ProductCategories[] | null => {
    const allProductCategoriesIds = searchResult?.pages.flatMap((product) => product.map((p) => p.categories?.[0]?.id));

    if (!allProductCategoriesIds?.length) return null;

    const getFilteredCats = allProductCategoriesIds
      .map((catId) => getCategoryInfo(catId))
      .filter((category): category is ProductCategories => category !== null);

    const uniqueCategories = Array.from(new Map(getFilteredCats.map((cat) => [cat.categoryName, cat])).values());

    return uniqueCategories;
  };

  return {
    searchText,
    isLoading: isLoadingProducts || isLoadingCategories,
    isFetched,
    isFetching: isFetchingProducts || isFetchingCategories,
    hasNextPage,
    cleanSearch,
    fetchNextPage,
    chosenCategory,
    groupedProducts,
    isFetchingNextPage,
    handleChoseCategory,
    getFilteredProducts,
    getProductCategories,
    noResults,
  };
};
